import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import JSZip from 'jszip';
import { db } from './firebase';

// Schema version 1: { exportedAt, schemaVersion, user: {uid, email},
// boards: [{id, name, ...topLevelFields, lists: [...]}], reminders: [...] }
//
// Mirrors the live queries in hooks/useData.js (boards, lists) and
// hooks/useReminders.js. One-shot getDocs — no listeners. Firestore
// Timestamps are serialised to ISO strings so the JSON round-trips
// cleanly through any standard parser.
export const SCHEMA_VERSION = 1;

export async function collectAll(user) {
  if (!user?.uid) throw new Error('collectAll: missing user');

  const [boards, reminders] = await Promise.all([
    fetchBoardsWithLists(user.uid),
    fetchReminders(user.uid),
  ]);

  return {
    exportedAt: new Date().toISOString(),
    schemaVersion: SCHEMA_VERSION,
    user: {
      uid: user.uid,
      email: user.email || null,
    },
    boards,
    reminders,
  };
}

async function fetchBoardsWithLists(uid) {
  const boardsQ = query(
    collection(db, 'users', uid, 'boards'),
    orderBy('createdAt', 'asc'),
  );
  const snap = await getDocs(boardsQ);
  const boards = snap.docs.map(d => ({ id: d.id, ...serialise(d.data()) }));

  const withLists = await Promise.all(boards.map(async (b) => {
    const listsSnap = await getDocs(
      collection(db, 'users', uid, 'boards', b.id, 'lists'),
    );
    const lists = listsSnap.docs.map(d => ({ id: d.id, ...serialise(d.data()) }));
    return { ...b, lists };
  }));

  return withLists;
}

async function fetchReminders(uid) {
  const snap = await getDocs(collection(db, 'users', uid, 'reminders'));
  return snap.docs.map(d => ({ id: d.id, ...serialise(d.data()) }));
}

// Recursively convert Firestore Timestamps (anything with .toDate) into
// ISO strings. Leaves primitives, arrays, and plain objects alone.
function serialise(value) {
  if (value == null) return value;
  if (typeof value !== 'object') return value;
  if (typeof value.toDate === 'function') {
    try { return value.toDate().toISOString(); } catch { return null; }
  }
  if (Array.isArray(value)) return value.map(serialise);
  const out = {};
  for (const [k, v] of Object.entries(value)) out[k] = serialise(v);
  return out;
}

// Triggers a browser download of `data` as pretty-printed JSON.
export function downloadAsJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  triggerDownload(blob, filename);
}

// Triggers a browser download of an already-built Blob.
export function downloadBlob(blob, filename) {
  triggerDownload(blob, filename);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Build a zip of plain-text files mirroring the user's board → list
// hierarchy. Companion to collectAll/downloadAsJson — same data, readable
// shape, but lossy: positions/sizes, reminder dates, share state, and
// list metadata are dropped. A top-level README.txt spells that out.
//
// Folder structure:
//   README.txt
//   boards/<board>/<list>.txt
//   reminders.txt
//
// Returns a Blob ready to hand to downloadBlob.
export async function collectAllAsZip(user) {
  const data = await collectAll(user);
  const zip = new JSZip();

  zip.file('README.txt', buildReadme(data));

  const boardsFolder = zip.folder('boards');
  const usedBoardNames = new Set();
  for (const board of data.boards) {
    const boardDirName = uniqueName(safeFilename(board.name || 'untitled board'), usedBoardNames);
    const dir = boardsFolder.folder(boardDirName);
    const usedListNames = new Set();
    // Sort lists by `order` (if present) so the zipped output mirrors
    // what the user sees in the sidebar.
    const lists = [...(board.lists || [])].sort((a, b) => {
      const ao = typeof a.order === 'number' ? a.order : Infinity;
      const bo = typeof b.order === 'number' ? b.order : Infinity;
      return ao - bo;
    });
    for (const list of lists) {
      const listFileBase = uniqueName(
        safeFilename(list.title || '(untitled)'),
        usedListNames,
      );
      dir.file(`${listFileBase}.txt`, list.content || '');
    }
  }

  zip.file('reminders.txt', buildRemindersText(data.reminders));

  return zip.generateAsync({ type: 'blob' });
}

function buildReadme(data) {
  return [
    'listicity — text export',
    '',
    `Exported: ${data.exportedAt}`,
    `Account:  ${data.user.email || data.user.uid}`,
    `Boards:   ${data.boards.length}`,
    `Reminders:${data.reminders.length}`,
    '',
    'What you have here',
    '------------------',
    'boards/<board name>/<list title>.txt   — each list as a plain text file.',
    'reminders.txt                           — one line per reminder.',
    '',
    'What is NOT in this export',
    '--------------------------',
    'This is the readable text version. The following are dropped:',
    '  • tile positions, sizes, and stacking order',
    '  • list metadata (created/updated timestamps, state)',
    '  • reminder parsed dates beyond the line itself',
    '  • board sharing state (collaborators, share tokens)',
    '',
    'For a full backup that round-trips back into listicity, use the',
    'JSON export option in the Account menu instead.',
    '',
  ].join('\n');
}

function buildRemindersText(reminders) {
  if (!reminders || reminders.length === 0) {
    return '(no reminders)\n';
  }
  const lines = ['# listicity reminders', ''];
  // Sort by parsedDate when present (undated reminders sink to the bottom).
  const sorted = [...reminders].sort((a, b) => {
    const ad = a.parsedDate || '~';
    const bd = b.parsedDate || '~';
    return ad < bd ? -1 : ad > bd ? 1 : 0;
  });
  for (const r of sorted) {
    const when = r.parsedDate || 'undated';
    const status = r.status || 'open';
    lines.push(`[${status}] ${when}  ${r.text || ''}`);
  }
  lines.push('');
  return lines.join('\n');
}

// Strip filesystem-hostile characters and trim length. Empty/blank names
// fall back to 'untitled' so we never emit a zero-length file name.
function safeFilename(name) {
  const trimmed = String(name).replace(/[\\/:*?"<>|\r\n\t]/g, ' ').trim();
  const collapsed = trimmed.replace(/\s+/g, ' ');
  if (!collapsed) return 'untitled';
  // 80 chars is plenty for human reading and well under any filesystem cap.
  return collapsed.slice(0, 80);
}

// Append " (2)", " (3)" etc. when a name collides with one already used
// in the same parent folder.
function uniqueName(base, used) {
  let candidate = base;
  let n = 2;
  while (used.has(candidate.toLowerCase())) {
    candidate = `${base} (${n++})`;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

// listicity-export-YYYY-MM-DD.{json,zip} — caller picks the extension to
// match what they're shipping.
export function defaultExportFilename(date = new Date(), ext = 'json') {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `listicity-export-${y}-${m}-${d}.${ext}`;
}
